import { createFileRoute, notFound, useParams } from "@tanstack/react-router";
import { ArrowLeft, Calendar, Tag } from "lucide-react";
import ReactMarkdown from "react-markdown";

import { useLang, useLangPrefix } from "@/contexts/lang";
import type { StrapiArticle } from "@/lib/articles";
import { getArticle, getCoverUrl } from "@/lib/articles";

export const Route = createFileRoute("/news/$id")({
	loader: async ({ params }) => {
		const article = await getArticle(params.id, "mk-MK");
		if (!article) throw notFound();
		return { article };
	},
	headers: () => ({
		"Cache-Control":
			"public, max-age=0, s-maxage=14400, stale-while-revalidate=86400",
	}),
	head: ({ loaderData }) => {
		const { article } = loaderData ?? {};
		if (!article) return {};
		const title = article.title;
		const description = article.excerpt;
		const coverUrl = getCoverUrl(article);
		const imageUrl =
			coverUrl ?? "https://makedonijaturist.com.mk/makedonijaturist.png";

		return {
			meta: [
				{ title: `${title} | Македонијатурист АД` },
				{ name: "description", content: description },
				{ property: "og:title", content: title },
				{ property: "og:description", content: description },
				{ property: "og:image", content: imageUrl },
				{ property: "og:type", content: "article" },
				{ name: "twitter:card", content: "summary_large_image" },
				{ name: "twitter:title", content: title },
				{ name: "twitter:description", content: description },
				{ name: "twitter:image", content: imageUrl },
			],
			links: [
				{
					rel: "canonical",
					href: `https://makedonijaturist.com.mk/news/${article.documentId}`,
				},
			],
			scripts: [
				{
					type: "application/ld+json",
					children: JSON.stringify({
						"@context": "https://schema.org",
						"@type": "NewsArticle",
						headline: title,
						description: description,
						image: imageUrl,
						datePublished: article.date,
						publisher: {
							"@type": "Organization",
							name: "Македонијатурист АД",
							logo: {
								"@type": "ImageObject",
								url: "https://makedonijaturist.com.mk/logo512.png",
							},
						},
					}),
				},
			],
		};
	},
	component: ArticlePage,
});

export function ArticlePageContent({ article }: { article: StrapiArticle }) {
	const t = useLang();
	const prefix = useLangPrefix();
	const coverUrl = getCoverUrl(article);

	return (
		<main className="bg-white">
			{/* Header section */}
			<section className="bg-primary text-white py-16">
				<div className="mx-auto max-w-4xl px-6">
					<a
						href={`${prefix}/news`}
						className="inline-flex items-center gap-2 text-sm font-medium text-white/70 hover:text-white transition-colors mb-8"
					>
						<ArrowLeft className="h-4 w-4" />
						{t.articlePage.backToNews}
					</a>

					<div className="flex items-center gap-3 mb-6">
						<span className="inline-flex items-center gap-1.5 rounded-full border border-white/20 bg-white/10 backdrop-blur-sm px-3 py-1.5 text-xs font-medium text-white">
							<Tag className="h-3 w-3" />
							{article.category}
						</span>
						<div className="flex items-center gap-2 text-sm text-white/60">
							<Calendar className="h-4 w-4" />
							<span>{article.date}</span>
						</div>
					</div>

					<h1 className="font-display text-4xl font-medium tracking-tight text-white md:text-5xl lg:text-6xl leading-[1.1]">
						{article.title}
					</h1>
				</div>
			</section>

			{/* Cover image */}
			{coverUrl && (
				<div className="mx-auto max-w-4xl px-6 mt-10">
					<img
						src={coverUrl}
						alt={article.cover?.alternativeText ?? article.title}
						className="w-full rounded-lg object-cover max-h-[480px]"
					/>
				</div>
			)}

			{/* Article content */}
			<article className="py-16 sm:py-20">
				<div className="mx-auto max-w-3xl px-6">
					{article.body ? (
						<div className="prose prose-slate max-w-none">
							<ReactMarkdown>{article.body}</ReactMarkdown>
						</div>
					) : (
						<p className="text-xl text-foreground font-light leading-relaxed">
							{article.excerpt}
						</p>
					)}

					{/* Back to news link */}
					<div className="mt-16 pt-8 border-t border-primary/5">
						<a
							href={`${prefix}/news`}
							className="inline-flex items-center gap-2 text-sm font-medium text-primary hover:text-accent transition-colors"
						>
							<ArrowLeft className="h-4 w-4" />
							{t.articlePage.backToAllNews}
						</a>
					</div>
				</div>
			</article>
		</main>
	);
}

function ArticlePage() {
	const { id } = useParams({ strict: false });
	const { article } = Route.useLoaderData();
	// Fallback: use the id from params if loader article doesn't match
	if (!article || article.documentId !== id) return null;
	return <ArticlePageContent article={article} />;
}
